import { useTranslation } from 'react-i18next';
import { Loader2, Star } from 'lucide-react';
import { useHospitals } from '@/features/hospitals';
import { localizedName } from '@/utils/localized';
import { MODAL_COLORS } from '@/components/modal';

interface HospitalAssignmentFieldProps {
  selectedIds: string[];
  primaryId: string | undefined;
  onChange: (ids: string[], primaryId?: string) => void;
}

export function HospitalAssignmentField({
  selectedIds,
  primaryId,
  onChange,
}: HospitalAssignmentFieldProps) {
  const { t, i18n } = useTranslation();
  const { data, isLoading } = useHospitals({ isActive: 'true', limit: 100 });
  const hospitals = data?.hospitals ?? [];

  const toggleHospital = (id: string) => {
    const isSelected = selectedIds.includes(id);
    if (isSelected) {
      const newIds = selectedIds.filter((h) => h !== id);
      const newPrimary = primaryId === id ? (newIds.length === 1 ? newIds[0] : undefined) : primaryId;
      onChange(newIds, newPrimary);
    } else {
      const newIds = [...selectedIds, id];
      const newPrimary = newIds.length === 1 ? newIds[0] : primaryId;
      onChange(newIds, newPrimary);
    }
  };

  const setPrimary = (id: string) => {
    onChange(selectedIds, id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: MODAL_COLORS.border }} />
      </div>
    );
  }

  if (hospitals.length === 0) {
    return (
      <div className="text-center py-3 text-sm text-gray-500">
        {t('hospitals.noHospitals')}
      </div>
    );
  }

  return (
    <div
      className="rounded-lg overflow-hidden max-h-48 overflow-y-auto"
      style={{
        border: `1px solid ${MODAL_COLORS.border}`,
      }}
    >
      {hospitals.map((hospital, index) => {
        const isSelected = selectedIds.includes(hospital.id);
        const isPrimary = primaryId === hospital.id;

        return (
          <div
            key={hospital.id}
            className="flex items-center gap-2 px-3 py-2 transition-colors"
            style={{
              backgroundColor: isSelected ? MODAL_COLORS.interactive : 'transparent',
              borderBottom: index < hospitals.length - 1 ? `1px solid ${MODAL_COLORS.border}40` : undefined,
            }}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggleHospital(hospital.id)}
              className="w-4 h-4 rounded cursor-pointer accent-primary-500 flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <span className="text-sm font-semibold block truncate">
                {localizedName(hospital, i18n.language)}
              </span>
              {hospital.city && (
                <span className="text-xs text-gray-500 block truncate">{hospital.city}</span>
              )}
            </div>
            {isSelected && (
              <button
                type="button"
                onClick={() => setPrimary(hospital.id)}
                className="flex-shrink-0 p-1 rounded-full transition-colors cursor-pointer"
                title={t('users.setPrimary')}
              >
                <Star
                  className="w-4 h-4"
                  fill={isPrimary ? '#f59e0b' : 'none'}
                  stroke={isPrimary ? '#f59e0b' : '#9ca3af'}
                />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
